import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  CharterCompanyDto,
  CharterLegalNature,
  CharterValueDto,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import {
  LEGAL_NATURE_LABEL,
  describeHeadOffice,
  formatCnpj,
  formatFiscalYearEnd,
  formatNire,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { dominantSource } from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';

interface CompanyRow {
  label: string;
  value: string;
  evidence: CharterValueDto<unknown> | null;
  wide?: boolean;
  /** Complemento em verde ou âmbar ao lado do valor (ex.: "igual ao cadastro"). */
  note?: { text: string; tone: 'ok' | 'warning' } | null;
}

/** Cartão "A empresa": razão social, CNPJ (comparado com o cadastro), tipo societário, NIRE, sede, prazo e objeto. */
@Component({
  selector: 'app-charter-company-card',
  imports: [CharterProvenanceComponent],
  templateUrl: './charter-company-card.component.html',
  styleUrl: './charter-company-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterCompanyCardComponent {
  private readonly dates = inject(DateFormatterService);

  readonly company = input.required<CharterCompanyDto>();
  readonly legalNature = input<CharterLegalNature | null>(null);

  protected readonly rows = computed<CompanyRow[]>(() => {
    const c = this.company();
    const nature = this.legalNature();
    const rows: CompanyRow[] = [
      { label: 'Razão social', value: c.corporateName.value || '—', evidence: c.corporateName },
      {
        label: 'CNPJ',
        value: formatCnpj(c.cnpj.value),
        evidence: c.cnpj,
        note:
          c.cnpjMatchesCompany === true
            ? { text: 'igual ao cadastro', tone: 'ok' }
            : c.cnpjMatchesCompany === false
              ? { text: 'diferente do cadastro', tone: 'warning' }
              : null,
      },
      {
        label: 'Tipo societário',
        value: nature ? LEGAL_NATURE_LABEL[nature] : '—',
        evidence: null,
      },
      { label: 'NIRE', value: formatNire(c.nire.value), evidence: c.nire },
    ];
    if (c.tradeName.value) {
      rows.push({ label: 'Nome fantasia', value: c.tradeName.value, evidence: c.tradeName });
    }
    rows.push(
      {
        label: 'Sede',
        value: describeHeadOffice(c.headOffice.city.value, c.headOffice.state.value),
        evidence: c.headOffice.city,
      },
      {
        label: 'Prazo de duração',
        value: this.describeDuration(c),
        evidence: c.durationKind,
      },
      {
        label: 'Fim do exercício social',
        value: formatFiscalYearEnd(c.fiscalYearEnd.value),
        evidence: c.fiscalYearEnd,
      },
      {
        label: 'Objeto social',
        value: c.corporatePurpose.value || '—',
        evidence: c.corporatePurpose,
        wide: true,
      }
    );
    if (c.headOffice.street.value) {
      rows.splice(rows.length - 1, 0, {
        label: 'Endereço',
        value: c.headOffice.street.value,
        evidence: c.headOffice.street,
        wide: true,
      });
    }
    return rows;
  });

  /** Fonte dominante do cartão (cabeçalho); valores de outro ato ganham o selo "alterado na <ato>". */
  protected readonly source = computed(() => dominantSource(this.rows().map(row => row.evidence)));

  private describeDuration(c: CharterCompanyDto): string {
    if (c.durationKind.value === 'indefinite') {
      return 'Indeterminado';
    }
    if (c.durationKind.value === 'fixed') {
      const end = c.durationEndDate.value
        ? this.dates.formatDateInstant(c.durationEndDate.value, true)
        : null;
      return end ? `Determinado, até ${end}` : 'Determinado';
    }
    return '—';
  }
}
